import { Signal } from "@char/aftercare";
import { getNotifications, getUnreadNotificationCount, markNotificationsSeen } from "../queries.ts";
import { RpcClient } from "./api.ts";
import { authenticatedAppViewRequest, authState } from "./auth.ts";
import { config } from "./config.ts";
import { canCallRpc } from "./permissions.ts";

export const unreadCount = new Signal<number | undefined>(undefined);

export function canUseNotifications(scopes: ReadonlySet<string>): boolean {
  return [getNotifications.id, getUnreadNotificationCount.id, markNotificationsSeen.id].every(
    (method) => canCallRpc(scopes, method, config.appViewService),
  );
}

export async function refreshUnread(signal?: AbortSignal): Promise<void> {
  const state = authState.get();
  if (state.status !== "authenticated" || !canUseNotifications(state.scopes)) {
    unreadCount.set(undefined);
    return;
  }
  try {
    const page = await new RpcClient(authenticatedAppViewRequest).call(
      getUnreadNotificationCount,
      { parameters: {}, ...(signal && { signal }) },
    );
    if (signal?.aborted) return;
    unreadCount.set(page.unreadCount);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return;
  }
}

export function notificationText(reason: string): string {
  switch (reason) {
    case "like":
      return "liked your post";
    case "repost":
      return "reposted your post";
    case "follow":
      return "followed you";
    case "mention":
      return "mentioned you";
    case "reply":
      return "replied to your post";
    case "quote":
      return "quoted your post";
    default:
      return "notified you";
  }
}

/** Second-person reason line for notification post rows, cf. notificationText. */
export function notificationContext(reason: string): string | undefined {
  switch (reason) {
    case "reply":
      return "Replied to you";
    case "mention":
      return "Mentioned you";
    case "quote":
      return "Quoted your post";
    default:
      return undefined;
  }
}
